import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PDFCoverPage, PDFFooter, PDFHeader } from './pdf-shared'
import { pdfColors, pdfStyles, severityColors, statusColors } from './pdf-theme'
import { fmtCurrency, fmtDate, fmtDateTime, labelSeverity, labelStatus, labelType } from './pdf-utils'

export interface RecommendationsDetailData {
  tenant: { id: string; name: string; slug: string; plan: string } | null
  generatedAt: string
  filters: {
    status: string | null
    type: string | null
    severity: string | null
    m365TenantId: string | null
    from: string | null
    to: string | null
  }
  recommendations: Array<{
    id: string
    type: string
    severity: string
    title: string
    description: string
    status: string
    estimated_monthly_saving: number
    projected_saving: number
    created_at: string
    applied_at: string | null
    dismissed_at: string | null
    license_display_name: string | null
    user_display_name: string | null
    m365_tenant_name: string | null
  }>
}

export function RecommendationsDetailPDF({ data }: { data: RecommendationsDetailData }) {
  const clientName = data.tenant?.name ?? 'Cliente'
  const totalProjected = data.recommendations.reduce((s, r) => s + (r.projected_saving ?? 0), 0)
  const totalMonthly = data.recommendations.reduce((s, r) => s + (r.estimated_monthly_saving ?? 0), 0)

  // Build a human-readable filter summary
  const filterParts: string[] = []
  if (data.filters.status) filterParts.push(`Estado: ${labelStatus(data.filters.status)}`)
  if (data.filters.type) filterParts.push(`Tipo: ${labelType(data.filters.type)}`)
  if (data.filters.severity) filterParts.push(`Severidad: ${labelSeverity(data.filters.severity)}`)
  if (data.filters.from) filterParts.push(`Desde: ${data.filters.from}`)
  if (data.filters.to) filterParts.push(`Hasta: ${data.filters.to}`)
  const filterSummary = filterParts.length ? filterParts.join(' · ') : 'Todas las recomendaciones'

  return (
    <Document
      title={`ROI365 - Detalle de Recomendaciones - ${clientName}`}
      author="ROI365"
      subject="Detalle de recomendaciones de optimizacion de licencias"
    >
      <PDFCoverPage
        reportTitle="Detalle de Recomendaciones"
        reportDescription="Listado completo de las recomendaciones de optimizacion identificadas en el tenant Microsoft 365 segun los filtros aplicados."
        clientName={clientName}
        period={filterSummary}
        generatedAt={fmtDateTime(data.generatedAt)}
      />

      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <PDFHeader title="Detalle de Recomendaciones" subtitle={clientName} />
        <PDFFooter />

        <Text style={pdfStyles.h2}>Resumen del filtro aplicado</Text>
        <Text style={pdfStyles.paragraph}>{filterSummary}</Text>

        <View style={pdfStyles.kpiGrid}>
          <View style={[pdfStyles.kpiCard, { width: '32%' }]}>
            <Text style={pdfStyles.kpiCardLabel}>Recomendaciones</Text>
            <Text style={pdfStyles.kpiCardValue}>{data.recommendations.length}</Text>
          </View>
          <View style={[pdfStyles.kpiCard, { width: '32%', borderLeftColor: pdfColors.brandSecondary }]}>
            <Text style={pdfStyles.kpiCardLabel}>Ahorro mensual sumado</Text>
            <Text style={pdfStyles.kpiCardValue}>{fmtCurrency(totalMonthly)}</Text>
          </View>
          <View style={[pdfStyles.kpiCard, { width: '32%', borderLeftColor: pdfColors.success }]}>
            <Text style={pdfStyles.kpiCardLabel}>Ahorro proyectado sumado</Text>
            <Text style={pdfStyles.kpiCardValue}>{fmtCurrency(totalProjected)}</Text>
          </View>
        </View>

        <Text style={pdfStyles.h2}>Listado</Text>

        {data.recommendations.length === 0 ? (
          <Text style={pdfStyles.paragraph}>No hay recomendaciones que cumplan los filtros aplicados.</Text>
        ) : (
          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeaderRow}>
              <Text style={[pdfStyles.tableHeaderCell, { width: '28%' }]}>Recomendacion</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '12%' }]}>Tipo</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '9%' }]}>Severidad</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '9%' }]}>Estado</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '14%' }]}>Licencia / Usuario</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '10%' }]}>Creada</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '8%', textAlign: 'right' }]}>Mensual</Text>
              <Text style={[pdfStyles.tableHeaderCell, { width: '10%', textAlign: 'right' }]}>Proyectado</Text>
            </View>
            {data.recommendations.map((r, idx) => (
              <View key={r.id} style={[pdfStyles.tableRow, idx % 2 === 1 ? pdfStyles.tableRowAlt : {}]} wrap={false}>
                <View style={[pdfStyles.tableCell, { width: '28%' }]}>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{r.title}</Text>
                </View>
                <Text style={[pdfStyles.tableCell, { width: '12%' }]}>{labelType(r.type)}</Text>
                <Text
                  style={[
                    pdfStyles.tableCell,
                    { width: '9%', color: severityColors[r.severity] ?? pdfColors.textPrimary, fontFamily: 'Helvetica-Bold' },
                  ]}
                >
                  {labelSeverity(r.severity)}
                </Text>
                <Text
                  style={[
                    pdfStyles.tableCell,
                    { width: '9%', color: statusColors[r.status] ?? pdfColors.textPrimary, fontFamily: 'Helvetica-Bold' },
                  ]}
                >
                  {labelStatus(r.status)}
                </Text>
                <View style={[pdfStyles.tableCell, { width: '14%' }]}>
                  <Text style={{ fontSize: 8.5 }}>{r.license_display_name ?? '—'}</Text>
                  {r.user_display_name ? (
                    <Text style={{ fontSize: 7.5, color: pdfColors.textTertiary }}>
                      {r.user_display_name}
                    </Text>
                  ) : null}
                </View>
                <Text style={[pdfStyles.tableCell, { width: '10%' }]}>{fmtDate(r.created_at)}</Text>
                <Text style={[pdfStyles.tableCell, { width: '8%', textAlign: 'right' }]}>
                  {fmtCurrency(r.estimated_monthly_saving)}
                </Text>
                <Text
                  style={[
                    pdfStyles.tableCell,
                    { width: '10%', textAlign: 'right', fontFamily: 'Helvetica-Bold', color: pdfColors.brandSecondary },
                  ]}
                >
                  {fmtCurrency(r.projected_saving)}
                </Text>
              </View>
            ))}
          </View>
        )}

        <Text style={[pdfStyles.small, { marginTop: 12 }]}>
          Listado limitado a las primeras 1000 recomendaciones que cumplen los filtros. Si necesitas
          mas registros, ajusta los filtros o exporta desde la vista de detalle de cada recomendacion.
        </Text>
      </Page>
    </Document>
  )
}
